import { Box, Checkbox, FormControl, Input, Option, Select, Typography } from "@mui/joy";
import CircularProgress from "@mui/material/CircularProgress";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import moment from "moment";
import React from "react";
import { useNavigate } from "react-router-dom";
import PortalTableLayout from "../components/PortalTableLayout";
import { isAdminRole, useRole } from "../Contexts/RoleContext";
import { Util } from "../Helpers/Util";

interface ServiceReportRow {
	service_reports_id: string;
	report_date: string;
	entered_by: string;
	building_count: number;
	elevator_count: number;
	finished_elevator_count: number;
	is_finished: boolean | number;
}

function listStatus(row: ServiceReportRow) {
	if (isFinished(row.is_finished)) return "Complete";
	const total = row.elevator_count ?? 0;
	const done = row.finished_elevator_count ?? 0;
	if (total === 0) return "Pending";
	return `${done}/${total} done`;
}

const columns = ["Date", "Entered By", "Buildings", "Status"];

function isFinished(value: boolean | number | null | undefined) {
	return value === true || value === 1;
}
const reportTypes = ["Repair", "Maintenance", "Sales", "Callback", "Unjustified Callback"];

interface MechanicOption {
	users_id: string;
	entered_by: string;
}

export default function ServiceReports() {
	const { effectiveRole } = useRole();
	const navigate = useNavigate();
	const [rows, setRows] = React.useState<ServiceReportRow[]>([]);
	const [mechanics, setMechanics] = React.useState<MechanicOption[]>([]);
	const [search, setSearch] = React.useState("");
	const [mechanic, setMechanic] = React.useState("");
	const [building, setBuilding] = React.useState("");
	const [reportDate, setReportDate] = React.useState("");
	const [reportType, setReportType] = React.useState("");
	const [showCompleted, setShowCompleted] = React.useState(false);
	const [loading, setLoading] = React.useState(true);
	const [error, setError] = React.useState("");
	const [orderBy, setOrderBy] = React.useState(0);
	const [desc, setDesc] = React.useState(true);
	const [page, setPage] = React.useState(0);
	const [pageSize, setPageSize] = React.useState(25);
	const staff = isAdminRole(effectiveRole);

	React.useEffect(() => {
		if (!staff) return;
		const handle = window.setTimeout(() => {
			setLoading(true);
			fetch("ServiceReport/GetServiceReports", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({
					search,
					usersId: mechanic || null,
					building,
					reportDate: reportDate || null,
					reportType: reportType || null,
					showCompleted,
				}),
			})
				.then((response) => response.json())
				.then((body) => {
					if (body && body.error) {
						setError(body.error);
						setRows([]);
					} else if (Array.isArray(body)) {
						setError("");
						setRows(body);
					} else {
						setError("");
						setRows(body?.reports ?? []);
						setMechanics(body?.mechanics ?? []);
					}
					setLoading(false);
				})
				.catch(() => {
					setError("Could not load service reports.");
					setLoading(false);
				});
		}, 300);
		return () => window.clearTimeout(handle);
	}, [search, mechanic, building, reportDate, reportType, showCompleted, staff]);

	const sorted = [...rows].sort((a, b) => {
		const dir = desc ? -1 : 1;
		switch (columns[orderBy]) {
			case "Entered By":
				return dir * (a.entered_by ?? "").localeCompare(b.entered_by ?? "");
			case "Buildings":
				return dir * ((a.building_count ?? 0) - (b.building_count ?? 0));
			case "Status":
				return dir * (Number(isFinished(a.is_finished)) - Number(isFinished(b.is_finished)));
			default:
				return dir * (moment(a.report_date).valueOf() - moment(b.report_date).valueOf());
		}
	});
	const pageRows = sorted.slice(page * pageSize, (page + 1) * pageSize);

	if (!staff) return <Typography>Service reports are not available for this login.</Typography>;

	return (
		<PortalTableLayout
			fillHeight
			toolbar={
				<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1.5, flexWrap: "wrap", backgroundColor: "background.surface", border: "1px solid", borderColor: "divider", p: 1.5 }}>
					<Typography level="title-lg" sx={{ m: 0 }}>
						Service Reports
					</Typography>
					<Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
						<FormControl>
							<Input
								placeholder="Search"
								value={search}
								onChange={(event) => {
									setSearch(event.target.value);
									setPage(0);
								}}
							/>
						</FormControl>
						<Select
							placeholder="Mechanic"
							value={mechanic || null}
							onChange={(_event, value) => {
								setMechanic(value ?? "");
								setPage(0);
							}}
							sx={{ minWidth: 160 }}>
							<Option value="">All mechanics</Option>
							{mechanics.map((item) => (
								<Option key={item.users_id} value={item.users_id}>
									{item.entered_by || item.users_id}
								</Option>
							))}
						</Select>
						<FormControl>
							<Input
								placeholder="Building"
								value={building}
								onChange={(event) => {
									setBuilding(event.target.value);
									setPage(0);
								}}
							/>
						</FormControl>
						<FormControl>
							<Input
								type="date"
								value={reportDate}
								onChange={(event) => {
									setReportDate(event.target.value);
									setPage(0);
								}}
							/>
						</FormControl>
						<Select
							placeholder="Type"
							value={reportType || null}
							onChange={(_event, value) => {
								setReportType(value ?? "");
								setPage(0);
							}}
							sx={{ minWidth: 180 }}>
							<Option value="">All types</Option>
							{reportTypes.map((type) => (
								<Option key={type} value={type}>
									{type}
								</Option>
							))}
						</Select>
						<Checkbox
							label="Show completed"
							checked={showCompleted}
							onChange={(event) => {
								setShowCompleted(event.target.checked);
								setPage(0);
							}}
						/>
					</Box>
				</Box>
			}
			pagination={{
				count: sorted.length,
				page,
				rowsPerPage: pageSize,
				onPageChange: (_event, next) => setPage(next),
				onRowsPerPageChange: (event) => {
					setPageSize(parseInt(event.target.value, 10));
					setPage(0);
				},
			}}>
			<Table stickyHeader size="small">
				<TableHead>
					<TableRow>
						{columns.map((column, index) => (
							<TableCell key={column}>
								<TableSortLabel
									active={orderBy === index}
									direction={orderBy === index && desc ? "desc" : "asc"}
									onClick={() => {
										setDesc(orderBy === index ? !desc : true);
										setOrderBy(index);
									}}>
									{column}
								</TableSortLabel>
							</TableCell>
						))}
					</TableRow>
				</TableHead>
				<TableBody>
					{loading && (
						<TableRow>
							<TableCell colSpan={columns.length} align="center">
								<CircularProgress size={28} />
							</TableCell>
						</TableRow>
					)}
					{!loading && error && (
						<TableRow>
							<TableCell colSpan={columns.length}>{error}</TableCell>
						</TableRow>
					)}
					{!loading && !error && pageRows.length === 0 && (
						<TableRow>
							<TableCell colSpan={columns.length}>No service reports.</TableCell>
						</TableRow>
					)}
					{!loading &&
						!error &&
						pageRows.map((row) => {
							const finished = isFinished(row.is_finished);
							return (
								<TableRow
									key={row.service_reports_id}
									hover
									sx={{ cursor: "pointer", backgroundColor: finished ? "#e8f5e9" : "#fff8e1" }}
									onClick={() => navigate("/servicereports/" + row.service_reports_id)}>
									<TableCell>{Util.Formatter(row.report_date, "D")}</TableCell>
									<TableCell>{row.entered_by}</TableCell>
									<TableCell>{row.building_count}</TableCell>
									<TableCell>{listStatus(row)}</TableCell>
								</TableRow>
							);
						})}
				</TableBody>
			</Table>
		</PortalTableLayout>
	);
}
